import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { base44 } from '../api/apiClient';
import { TrendingUp } from 'lucide-react';

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [inviteData, setInviteData] = useState(null);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ first_name: '', last_name: '', username: '', password: '', confirm_password: '' });
  const [submitting, setSubmitting] = useState(false);

  const inputClassName = "w-full px-3.5 py-2.5 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1a4731]/25 focus:border-[#1a4731]/40 transition-all";
  const labelClassName = "block text-sm font-medium text-slate-600 mb-1.5";

  useEffect(() => {
    base44.organizations.verifyInvite(token)
      .then((data) => setInviteData(data))
      .catch(() => setError('This invitation link is invalid or has already been used.'))
      .finally(() => setVerifying(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirm_password) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await base44.organizations.acceptInvite(token, {
        username: form.username,
        password: form.password,
        first_name: form.first_name,
        last_name: form.last_name,
      });
      navigate('/login', { state: { message: 'Account created. You can now log in.' } });
    } catch (err) {
      const data = err.response?.data;
      if (data?.username) setError(data.username);
      else setError(data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8f7f4]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 border-t-[#1a4731]" />
      </div>
    );
  }

  if (!inviteData) {
    return (
      <div className="min-h-screen flex bg-[#f8f7f4]">
        <div className="hidden lg:flex lg:w-[45%] flex-col justify-between p-12 relative overflow-hidden bg-[#1a4731]">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/5" />
          <div className="absolute -bottom-32 -left-16 w-80 h-80 rounded-full bg-white/5" />
          <div className="absolute top-1/2 right-16 w-44 h-44 rounded-full bg-white/[0.03]" />

          <div className="flex items-center gap-3 relative z-10">
            <div className="w-11 h-11 bg-white/15 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/10">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <span className="text-white font-bold text-xl tracking-tight">Carlo</span>
          </div>

          <div className="relative z-10">
            <h1 className="text-5xl font-bold text-white leading-[1.1] mb-5 tracking-tight">
              Smart<br />Restaurant<br />Operations
            </h1>
            <p className="text-white/50 text-lg leading-relaxed max-w-xs">
              Manage your sales, inventory, staff and analytics — all in one place.
            </p>
          </div>

          <p className="text-white/25 text-sm relative z-10">© {new Date().getFullYear()} Carlo</p>
        </div>

        <div className="flex-1 flex items-center justify-center p-6 sm:p-8">
          <div className="w-full max-w-[380px]">
            <div className="flex items-center gap-3 mb-10 lg:hidden">
              <div className="w-11 h-11 bg-[#1a4731] rounded-xl flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl text-slate-900 tracking-tight">Carlo</span>
            </div>
            <p className="text-2xl font-bold text-slate-900 mb-2">Invalid invitation</p>
            <p className="text-slate-400 text-sm">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-[#f8f7f4]">
      <div className="hidden lg:flex lg:w-[45%] flex-col justify-between p-12 relative overflow-hidden bg-[#1a4731]">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/5" />
        <div className="absolute -bottom-32 -left-16 w-80 h-80 rounded-full bg-white/5" />
        <div className="absolute top-1/2 right-16 w-44 h-44 rounded-full bg-white/[0.03]" />

        <div className="flex items-center gap-3 relative z-10">
          <div className="w-11 h-11 bg-white/15 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/10">
            <TrendingUp className="w-5 h-5 text-white" />
          </div>
          <span className="text-white font-bold text-xl tracking-tight">Carlo</span>
        </div>

        <div className="relative z-10">
          <h1 className="text-5xl font-bold text-white leading-[1.1] mb-5 tracking-tight">
            Smart<br />Restaurant<br />Operations
          </h1>
          <p className="text-white/50 text-lg leading-relaxed max-w-xs">
            Manage your sales, inventory, staff and analytics — all in one place.
          </p>
        </div>

        <p className="text-white/25 text-sm relative z-10">© {new Date().getFullYear()} Carlo</p>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 sm:p-8">
        <div className="w-full max-w-[420px]">
          <div className="flex items-center gap-3 mb-10 lg:hidden">
            <div className="w-11 h-11 bg-[#1a4731] rounded-xl flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-xl text-slate-900 tracking-tight">Carlo</span>
          </div>

          <h1 className="text-2xl font-bold text-slate-900 mb-1.5">Create your account</h1>
          <p className="text-slate-400 text-sm mb-8">
            You have been invited to join <strong>{inviteData.restaurant}</strong>
          </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className={labelClassName}>Email</label>
            <input
              type="text"
              value={inviteData.email}
              disabled
              className={`${inputClassName} bg-slate-50 text-slate-500`}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClassName}>First name</label>
              <input
                type="text"
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                className={inputClassName}
              />
            </div>
            <div>
              <label className={labelClassName}>Last name</label>
              <input
                type="text"
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                className={inputClassName}
              />
            </div>
          </div>

          <div>
            <label className={labelClassName}>Username</label>
            <input
              type="text"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              required
              className={inputClassName}
            />
          </div>

          <div>
            <label className={labelClassName}>Password</label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
              className={inputClassName}
            />
          </div>

          <div>
            <label className={labelClassName}>Confirm password</label>
            <input
              type="password"
              value={form.confirm_password}
              onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
              required
              className={inputClassName}
            />
          </div>

          {error && (
            <div className="bg-red-50 text-red-700 text-sm px-3 py-2 rounded-lg border border-red-200">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#1a4731] hover:bg-[#153a27] disabled:opacity-50 text-white text-sm font-semibold py-2.5 rounded-lg transition-colors"
          >
            {submitting ? 'Creating account...' : 'Create account'}
          </button>
        </form>
        </div>
      </div>
    </div>
  );
}
